import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/header';
import {
  Loader2,
  Route,
  UserPlus,
  Check,
  X,
  AlertTriangle,
  ArrowLeft,
} from 'lucide-react';
import { useTranslationWithLanguage } from '../context/language-context';
import { InspectionCard } from '../components/inspection-card';
import { useAuth } from '../context/auth-context'; // Use the exported hook
import { createPortal } from 'react-dom';
import { Button } from '../components/ui/button';

// Define BASE_URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// Define the structure for the fetched inspection data
interface ApiInspection {
  id: string; // Task ID
  establishment_id: string;
  establishment_name_en: string;
  establishment_name_ar: string;
  emirate_name_en: string;
  emirate_name_ar: string;
  inspection_type_name_en: string;
  inspection_type_name_ar: string;
  risk_grade: string | null; // Can be null
  task_status_id: string;
  task_status_name_en: string;
  task_status_name_ar: string;
  latitude?: string;
  longitude?: string;
  params?: any;
  // Add other relevant fields if needed based on API response
}

// No need for Establishment interface as we're not using route optimization

export default function SuggestedInspectionsPage() {
  const navigate = useNavigate();
  const { t, language } = useTranslationWithLanguage();
  const { getAccessToken } = useAuth(); // Use the hook to get access token function
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inspections, setInspections] = useState<ApiInspection[]>([]);

  // New state for bulk assign functionality
  const [selectedInspections, setSelectedInspections] = useState<
    string[]
  >([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] =
    useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [bulkAssignResults, setBulkAssignResults] = useState<{
    success: number;
    failed: number;
    total: number;
  }>({ success: 0, failed: 0, total: 0 });
  const [showBulkAssignToast, setShowBulkAssignToast] =
    useState(false);
  const [bulkAssignToastType, setBulkAssignToastType] = useState<
    'success' | 'error'
  >('success');
  const [bulkAssignToastMessage, setBulkAssignToastMessage] =
    useState('');

  // State for countdown timer after bulk assign
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(5);

  // State for tracking total points
  const [totalSelectedPoints, setTotalSelectedPoints] = useState(0);

  // Fetch inspections from API
  useEffect(() => {
    const fetchInspections = async () => {
      const currentToken = getAccessToken();
      if (!currentToken) {
        setError(t('errors.notAuthenticated'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch suggestion tasks (status 2)
        await fetchTasksByStatus('2', currentToken);
      } catch (err) {
        console.error(`Error fetching inspections:`, err);
        setError(
          err instanceof Error
            ? err.message
            : t('errors.unknownError')
        );
      } finally {
        setLoading(false);
      }
    };

    // Helper function to fetch tasks by status
    const fetchTasksByStatus = async (
      taskStatusId: string,
      token: string
    ) => {
      const conditionParam = {
        rules: [
          {
            operator: 'in',
            field: 'task_status_id',
            value: [taskStatusId],
          },
        ],
      };

      const params = new URLSearchParams({
        condition: JSON.stringify(conditionParam),
        // Add other necessary params like page, limit if needed
        // 'page[limit]': '50',
      });

      const response = await fetch(
        `${BASE_URL}/jsonapi/transform/task_list?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `${t('errors.failedToFetch')} (${response.status} ${
            response.statusText
          })`
        );
      }

      const data = await response.json();

      // Debug the API response structure
      console.log('API Response:', data);
      if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
        console.log('First inspection item:', data.data[0]);
        // Check if inspection_type_name_en exists
        if (!data.data[0].inspection_type_name_en) {
          console.warn('inspection_type_name_en is missing in the API response');
          // Try to find where inspection type might be stored
          console.log('Available fields:', Object.keys(data.data[0]));

          // Map the data with inspection type fallbacks and proper risk calculation
          const mappedData = data.data.map((item: any) => {
            // Check if inspection type is in params or another nested object
            const inspectionTypeEn =
              item.inspection_type_name_en ||
              (item.params?.inspection_type_display) ||
              (item.inspection_type?.name_en) ||
              t('inspections.regularInspection', 'Regular Inspection');

            const inspectionTypeAr =
              item.inspection_type_name_ar ||
              (item.params?.inspection_type_display) ||
              (item.inspection_type?.name_ar) ||
              'تفتيش منتظم';

            // Calculate risk grade using the provided function
            const calculateRiskGrade = (item: any): string => {
              if (item.params.risk_category) return item.params.risk_category;
              return 'HIGH';
            };

            // Use the provided risk grade or calculate it
            let riskGrade = calculateRiskGrade(item);

            console.log(`Item ${item.id} risk calculation:`, {
              original_risk_grade: item.risk_grade,
              params_risk_category: item.params?.risk_category,
              risk_score: item.risk_score,
              calculated_risk_grade: riskGrade
            });

            // Calculate risk category based on risk grade
            let riskCategory;
            if (riskGrade === 'A' || riskGrade === 'B') {
              riskCategory = t('risk.low', 'LOW');
            } else if (riskGrade === 'C' || riskGrade === 'D') {
              riskCategory = t('risk.medium', 'MEDIUM');
            } else {
              riskCategory = t('risk.high', 'HIGH');
            }

            return {
              ...item,
              inspection_type_name_en: inspectionTypeEn,
              inspection_type_name_ar: inspectionTypeAr,
              risk_grade: riskGrade,
              risk_category: riskCategory
            };
          });

          console.log('Mapped data with risk grades:', mappedData.map(item => ({
            id: item.id,
            risk_grade: item.risk_grade,
            risk_category: item.risk_category
          })));

          setInspections(mappedData);
        } else {
          // These are suggestion tasks (status 2)
          setInspections(data.data);
        }
      } else {
        console.warn('Unexpected API response structure:', data);
        setInspections([]);
      }
    };

    fetchInspections();
  }, [getAccessToken, t]); // Depend on getAccessToken function and t

  // No route optimization functions needed here

  // Function to handle selection of an inspection
  const handleInspectionSelect = (id: string) => {
    setSelectedInspections((prev) => {
      if (prev.includes(id)) {
        // Deselect inspection
        const newSelected = prev.filter(
          (inspectionId) => inspectionId !== id
        );
        // Recalculate total points
        calculateTotalPoints(newSelected);
        return newSelected;
      } else {
        // Select inspection
        const newSelected = [...prev, id];
        // Recalculate total points
        calculateTotalPoints(newSelected);
        return newSelected;
      }
    });
  };

  // Function to calculate total points of selected inspections
  const calculateTotalPoints = (selectedIds: string[]) => {
    let total = 0;

    // Find each selected inspection in the inspections array and sum up points
    selectedIds.forEach((id) => {
      const inspection = inspections.find((insp) => insp.id === id);
      if (inspection) {
        // Default to 1.1 points if not specified
        total += 1.1;
      }
    });

    setTotalSelectedPoints(total);
  };

  // Function to handle bulk assignment
  const handleBulkAssign = () => {
    if (selectedInspections.length > 0) {
      setShowBulkAssignDialog(true);
    }
  };

  // Function to confirm and process bulk assignment
  const confirmBulkAssignment = async () => {
    setIsBulkAssigning(true);
    setShowBulkAssignDialog(false);

    const total = selectedInspections.length;
    let success = 0;
    let failed = 0;

    // Reset results
    setBulkAssignResults({ success: 0, failed: 0, total });

    const currentToken = getAccessToken();
    if (!currentToken) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(t('errors.notAuthenticated'));
      setShowBulkAssignToast(true);
      setIsBulkAssigning(false);
      return;
    }

    // Process each selected inspection sequentially
    for (const inspectionId of selectedInspections) {
      try {
        // Call the API to assign the task
        const response = await fetch(
          `${BASE_URL}/sdp/rules/api/v2/task/actions/self_assign`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
            body: JSON.stringify({
              task_id: inspectionId,
            }),
          }
        );

        const data = await response.json();

        if (response.ok && data.status === true) {
          success++;
        } else {
          failed++;
          console.error('Failed to assign task:', inspectionId, data);
        }
      } catch (error) {
        failed++;
        console.error('Error assigning task:', inspectionId, error);
      }

      // Update results after each assignment
      setBulkAssignResults({ success, failed, total });
    }

    // Show final results toast
    if (failed === 0) {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(
        t('common.bulkAssignSuccess', { count: success })
      );
    } else if (success === 0) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(
        t('common.bulkAssignFailed', { count: failed })
      );
    } else {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(
        t('common.bulkAssignPartial', { success, failed })
      );
    }

    setShowBulkAssignToast(true);
    setIsBulkAssigning(false);

    // Clear selections after assignment
    setSelectedInspections([]);
    setTotalSelectedPoints(0);

    // Start countdown timer if at least one assignment was successful
    if (success > 0) {
      // Trigger a refresh event
      if (window.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent('inspection:bulk-assigned', {
            detail: { count: success },
          })
        );
      }

      // Start countdown timer
      setShowCountdown(true);
      setCountdownSeconds(30);

      // Set up interval to update countdown
      const countdownInterval = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowCountdown(false);
            // Refresh the data after countdown
            setLoading(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Bulk Assignment Confirmation Dialog
  const BulkAssignmentDialog = () => {
    if (!showBulkAssignDialog) return null;

    return (
      <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-2">
            {t('common.confirmBulkAssignment')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('common.bulkAssignmentConfirmMessage', {
              count: selectedInspections.length,
            })}
          </p>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkAssignDialog(false)}
              className="border-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-[#b89256] text-white hover:opacity-90"
              onClick={confirmBulkAssignment}
            >
              <Check className="w-4 h-4 mr-2" />
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Toast component for bulk assignment notifications
  const BulkAssignToast = () => {
    if (!showBulkAssignToast) return null;

    return createPortal(
      <div className="fixed bottom-4 right-4 z-[9999]">
        <div
          className={`rounded-md shadow-md p-4 flex items-center ${
            bulkAssignToastType === 'success'
              ? 'bg-green-50 border-l-4 border-green-500'
              : 'bg-red-50 border-l-4 border-red-500'
          }`}
        >
          <div
            className={`mr-3 ${
              bulkAssignToastType === 'success'
                ? 'text-green-500'
                : 'text-red-500'
            }`}
          >
            {bulkAssignToastType === 'success' ? (
              <Check className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1">
            <p
              className={`text-sm font-medium ${
                bulkAssignToastType === 'success'
                  ? 'text-green-800'
                  : 'text-red-800'
              }`}
            >
              {bulkAssignToastMessage}
            </p>
          </div>
          <button
            onClick={() => setShowBulkAssignToast(false)}
            className="ml-4 text-gray-400 hover:text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div
      className="flex flex-col min-h-screen bg-gray-50"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Header is hidden as requested */}

      {/* Navigation buttons with selection info card in between */}
      <div className="px-4 pt-4">
        <div className="flex justify-between items-center mb-4">
          {/* <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => navigate('/plan')}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('navigation.back', 'Back')}
          </Button> */}

          {/* Selection info card - Only show when items are selected */}
          {selectedInspections.length > 0 && (
            <div className="flex-1 mx-2 bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-3 py-1.5">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedInspections.length} {selectedInspections.length === 1 ? t('common.item', 'item') : t('common.items', 'items')}
                  </span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="text-sm font-medium text-blue-600">
                    {totalSelectedPoints.toFixed(1)}/10
                  </span>
                </div>
                <Button
                  size="sm"
                  className="h-7 py-0 px-2 bg-[#b89256] text-white hover:opacity-90 text-xs"
                  onClick={handleBulkAssign}
                  disabled={selectedInspections.length === 0 || isBulkAssigning}
                >
                  {isBulkAssigning ? (
                    <span className="flex items-center">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                      {t('common.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <UserPlus className="w-3 h-3 mr-1" />
                      {t('common.assignToMe', 'Assign to me')}
                    </span>
                  )}
                </Button>
              </div>
              {/* Progress bar at the bottom */}
              <div className="w-full h-1 bg-gray-100">
                <div
                  className="h-1 bg-[#b89256]"
                  style={{
                    width: `${Math.min((totalSelectedPoints / 10) * 100, 100)}%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => navigate('/route-optimization')}
          >
            <Route className="w-4 h-4 mr-1" />
            {t('filters.routeOptimization')}
          </Button> */}
        </div>
      </div>

      {/* Main content container */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="p-4">
          {/* Countdown timer */}
          {showCountdown && (
            <div className="mb-4 text-center bg-blue-50 p-2 rounded-md">
              <p className="text-blue-800 text-sm">
                {t('common.reloadingIn')} {countdownSeconds}{' '}
                {t('common.seconds')}
              </p>
            </div>
          )}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-[var(--color-gold-500)] animate-spin mb-4" />
              <p className="text-gray-500">
                {t('messages.loadingData')}
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-red-600">
              <p>
                {t('errors.error')}: {error}
              </p>
            </div>
          ) : (
            // Suggestions content
            inspections.length > 0 ? (
              // Display fetched inspections using InspectionCard
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inspections.map((inspection) => (
                  <InspectionCard
                    key={inspection.id}
                    id={inspection.id}
                    establishment_id={inspection.establishment_id}
                    establishment={{
                      id: inspection.establishment_id, // Use establishment_id from task
                      name_en: inspection.establishment_name_en,
                      name_ar: inspection.establishment_name_ar,
                    }}
                    emirate={{
                      id: 0, // Dummy ID as not provided by API
                      name_en: inspection.emirate_name_en,
                      name_ar: inspection.emirate_name_ar,
                    }}
                    inspection_type={{
                      id: 0, // Dummy ID as not provided by API
                      name_en: inspection.inspection_type_name_en,
                      name_ar: inspection.inspection_type_name_ar,
                    }}
                    risk_grade={inspection.risk_grade ?? t('risk.defaultGrade', 'low')} // Provide default if null
                    risk_category={inspection.params?.risk_category} // Pass the risk category with optional chaining
                    status={inspection.task_status_id} // Pass the status ID string ('2')
                    language={language}
                    context={t('inspections.contextSuggested', 'suggestedInspections')} // Pass context to show it's in SuggestedInspections
                    onSelect={handleInspectionSelect} // Pass selection handler
                    isSelected={selectedInspections.includes(
                      inspection.id
                    )} // Pass selection state
                  />
                ))}
              </div>
            ) : (
              // Display message if no inspections are found
              <div className="text-center py-12 text-gray-500">
                <p>{t('inspections.noSuggestedInspectionsFound')}</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Bulk Assignment Dialog */}
      <BulkAssignmentDialog />

      {/* Bulk Assignment Toast */}
      <BulkAssignToast />
    </div>
  );
}
